/**
 * SidebarContextSelector — v1.3.9
 * Selettori OPZIONALI Società + Unità Locale nella sidebar.
 *
 * Logica:
 * - Entrambi i selettori sono OPZIONALI: l'utente può lasciarli vuoti
 *   per operare globalmente (es. aggiungere nuova azienda/UL).
 * - Se viene selezionata una UL → l'app filtra tutto su quella UL.
 * - Se non è selezionato nulla → l'app mostra tutto (tutte le aziende/UL accessibili).
 * - Il "gate locale" per HACCP Schede Prodotto / Libro Ingredienti rimane attivo
 *   (richiedono comunque UL specifica per il template multi-tenant).
 */
import { useEffect, useMemo, useState } from 'react';
import { Building2, MapPin, X, ChevronsDown } from 'lucide-react';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';
import { useAuth } from '../contexts/AuthContext';
import { aziendeApi, pdvApi } from '../lib/api';

export default function SidebarContextSelector() {
  const { user } = useAuth();
  const {
    currentAziendaId,
    currentPdvId,
    setCurrentAziendaId,
    setCurrentPdvId,
    clearCurrentPdv,
  } = useCurrentPdv();

  const [aziende, setAziende] = useState([]);
  const [pdvList, setPdvList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Carica aziende accessibili all'utente
  useEffect(() => {
    let aborted = false;
    aziendeApi
      .list()
      .then((r) => { if (!aborted) setAziende(r.data || []); })
      .catch(() => { if (!aborted) setAziende([]); })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [user?.id]);

  // Carica tutte le UL accessibili (verrà filtrato lato client per azienda selezionata)
  const [pdvLoaded, setPdvLoaded] = useState(false);
  useEffect(() => {
    let aborted = false;
    pdvApi
      .getAll()
      .then((r) => { if (!aborted) { setPdvList(r.data || []); setPdvLoaded(true); } })
      .catch(() => { if (!aborted) { setPdvList([]); setPdvLoaded(true); } });
    return () => { aborted = true; };
  }, [user?.id]);

  // UL filtrate per azienda selezionata (se nessuna azienda scelta → tutte)
  const filteredPdv = useMemo(() => {
    if (!currentAziendaId) return pdvList;
    return pdvList.filter((p) => p.azienda_id === currentAziendaId);
  }, [pdvList, currentAziendaId]);

  // Quando l'azienda corrente non contiene più la UL corrente, sgancia la UL.
  // v1.3.10 — guard: NON azzerare finché pdvList non è stato caricato (evita race condition
  // su navigation che azzerava per errore il currentPdvId persistito in sessionStorage).
  useEffect(() => {
    if (!pdvLoaded) return;
    if (!currentPdvId) return;
    if (!currentAziendaId) return;
    const stillValid = filteredPdv.some((p) => p.id === currentPdvId);
    if (!stillValid) {
      setCurrentPdvId(null);
    }
  }, [pdvLoaded, currentAziendaId, currentPdvId, filteredPdv, setCurrentPdvId]);

  const handleAziendaChange = (e) => {
    const val = e.target.value;
    setCurrentAziendaId(val || null);
    // setCurrentAziendaId azzera il PDV automaticamente
  };

  const handlePdvChange = (e) => {
    const val = e.target.value;
    setCurrentPdvId(val || null);
    // Se l'utente sceglie una UL senza aver scelto azienda, sincronizza l'azienda
    if (val) {
      const found = pdvList.find((p) => p.id === val);
      if (found?.azienda_id && found.azienda_id !== currentAziendaId) {
        setCurrentAziendaId(found.azienda_id);
      }
    }
  };

  const hasFilter = !!currentAziendaId || !!currentPdvId;

  return (
    <div
      className="px-4 py-3 border-b border-border bg-gradient-to-b from-slate-50/80 to-transparent dark:from-slate-900/40"
      data-testid="sidebar-context-selector"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Contesto operativo
        </span>
        {hasFilter && (
          <button
            type="button"
            onClick={clearCurrentPdv}
            className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5"
            title="Rimuovi entrambi i filtri (mostra tutto)"
            data-testid="ctx-clear-all-btn"
          >
            <X className="w-3 h-3" /> Tutte
          </button>
        )}
      </div>

      {/* Selettore Società */}
      <label className="block text-[11px] font-medium text-foreground/70 mb-1 flex items-center gap-1">
        <Building2 className="w-3 h-3" /> Società
      </label>
      <div className="relative mb-2">
        <select
          value={currentAziendaId || ''}
          onChange={handleAziendaChange}
          disabled={loading}
          data-testid="sidebar-ctx-azienda-select"
          className="w-full text-xs px-2 py-1.5 pr-7 border rounded-md bg-white dark:bg-slate-900 outline-none focus:ring-1 focus:ring-primary appearance-none cursor-pointer"
        >
          <option value="">— Tutte le società —</option>
          {aziende.map((a) => (
            <option key={a.id} value={a.id}>
              {a.denominazione || a.name || 'Senza nome'}
            </option>
          ))}
        </select>
        <ChevronsDown className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      </div>

      {/* Selettore Unità Locale */}
      <label className="block text-[11px] font-medium text-foreground/70 mb-1 flex items-center gap-1">
        <MapPin className="w-3 h-3" /> Unità Locale
      </label>
      <div className="relative">
        <select
          value={currentPdvId || ''}
          onChange={handlePdvChange}
          disabled={loading || filteredPdv.length === 0}
          data-testid="sidebar-ctx-pdv-select"
          className="w-full text-xs px-2 py-1.5 pr-7 border rounded-md bg-white dark:bg-slate-900 outline-none focus:ring-1 focus:ring-primary appearance-none cursor-pointer disabled:opacity-50"
        >
          <option value="">
            {filteredPdv.length === 0
              ? '— Nessuna UL —'
              : '— Tutte le UL —'}
          </option>
          {filteredPdv.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}{p.city ? ` · ${p.city}` : ''}
            </option>
          ))}
        </select>
        <ChevronsDown className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      </div>

      {/* Hint */}
      <p className="text-[10px] text-muted-foreground/80 mt-2 leading-tight">
        {hasFilter
          ? '✓ Filtro attivo: vedi solo i dati selezionati.'
          : 'Nessun filtro: vedi tutto. Seleziona per operare su una UL.'}
      </p>
    </div>
  );
}
